import axios from "axios";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import { Typography, Flex, notification, Button, Modal, Steps } from "antd";
import Layout from "../layout/Layout";
import {
	CustomSearch,
	CustomSpinner,
	CustomTable,
} from "../utils/CustomComponents";
import dayjs from "dayjs";

const { Text } = Typography;

const OrderRelocationReport = ({ history }) => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(false);
	const [jobList, setJobList] = useState([]);

	const [term, setTerm] = useState("");
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());

	const [showHistory, setShowHistory] = useState(false);
	const [selectedHistory, setSelectedHistory] = useState([]);

	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Job ID",
			dataIndex: "jobId",
			key: "jobId",
			render: (value, record) => {
				return <Text strong>{value}</Text>;
			},
		},
		{
			title: "Client",
			dataIndex: "client",
			key: "client",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Seller",
			dataIndex: "seller",
			key: "seller",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Origin Plant",
			dataIndex: "originPlant",
			key: "originPlant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Current Plant",
			dataIndex: "currentPlant",
			key: "currentPlant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "",
			dataIndex: "edit",
			key: "edit",
			render: (_, record) => (
				<Button
					type="link"
					onClick={() => {
						setSelectedHistory(record.history);
						setShowHistory(true);
					}}
				>
					View
				</Button>
			),
		},
	];

	useEffect(() => {
		getJobs();
	}, [term, startDate, endDate]);

	const getJobs = async () => {
		try {
			setLoadingTable(true);

			const start = `${startDate.year()}-${
				startDate.month() + 1
			}-${startDate.date()}`;
			const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

			const url =
				api +
				`/job/get/plant/relocation?startDate=${start}&endDate=${end}&term=${term}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);

			if (data.success) {
				const payload = data.payload;
				setJobList(
					payload.map((item) => ({
						key: item._id,
						date: dayjs(item.scheduledDate).format("MM/DD/YYYY"),
						jobId: item.number,
						client: item.customer?.name || "N/A",
						seller: item.seller?.name || "N/A",
						originPlant:
							item.plantChangedHistory?.[0]?.prevPlant?.name || "N/A",
						currentPlant:
							item.plantChangedHistory?.[item.plantChangedHistory.length - 1]
								?.nextPlant?.name || "N/A",
						history: item.plantChangedHistory || [],
					})),
				);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
			setLoadingTable(false);
		} catch (err) {
			console.error(err.message);
			return null;
		}
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const onSearch = (value, _e, info) => {
		if (info?.source === "input") {
			setTerm(value);
		}
	};

	const RenderSearch = () => {
		return (
			<CustomSearch
				onSearch={onSearch}
				loading={loading || loadingTable}
				startDate={startDate}
				endDate={endDate}
				onRangeChange={onRangeChange}
				dateFormat={"MM/DD/YYYY"}
			/>
		);
	};

	const RenderTable = () => {
		return (
			<Flex gap={"middle"} vertical>
				<CustomTable
					columns={columns}
					data={jobList}
					loading={loadingTable}
					isScroll={true}
				/>
			</Flex>
		);
	};

	return (
		<Layout title="Order Relocation Report" description="Report">
			<Flex vertical gap={"large"}>
				<RenderSearch />
				{loading ? <CustomSpinner /> : <RenderTable />}
				{contextHolder}
				<Modal
					open={showHistory}
					centered
					title="Relocation History"
					onCancel={() => setShowHistory(false)}
					footer={null}
					width={700}
				>
					<Steps
						direction="vertical"
						current={selectedHistory.length - 1}
						items={selectedHistory.map((item) => ({
							title: `${item.prevPlant?.name ?? "N/A"} → ${
								item.nextPlant?.name ?? "N/A"
							}`,
							description: (
								<>
									<div>
										<strong>Date:</strong>{" "}
										{dayjs(item.createdAt).format("MM/DD/YYYY hh:mm A")}
									</div>

									<div>
										<strong>Changed By:</strong> {item.user?.name ?? "N/A"}
									</div>

									{item.reason && (
										<div>
											<strong>Reason:</strong> {item.reason}
										</div>
									)}
								</>
							),
						}))}
					/>
				</Modal>
			</Flex>
		</Layout>
	);
};

export default OrderRelocationReport;
